import React from 'react';
import {
  FileText,
  CalendarDays,
  Clock,
  CreditCard,
  AlertTriangle,
  ShieldCheck,
  RefreshCcw,
  Scale,
  Mail,
  MapPin,
  CheckCircle2,
  Ban,
  HeartHandshake
} from 'lucide-react';
import { useLanguage } from '../i18n';

const TermosServico = () => {
  const { language } = useLanguage();
  const isPT = language === 'pt';

  const styles = {
    page: {
      minHeight: '100vh',
      background:
        'linear-gradient(180deg, #F8F5EF 0%, #F3EEE5 50%, #F8F5EF 100%)',
      color: '#2C2924'
    },

    container: {
      maxWidth: '1050px',
      margin: '0 auto',
      padding: '80px 24px 100px'
    },

    hero: {
      maxWidth: '760px',
      margin: '0 auto 56px',
      textAlign: 'center'
    },

    eyebrow: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: '#9B7B3E',
      fontSize: '12px',
      fontWeight: '600',
      letterSpacing: '0.18em',
      textTransform: 'uppercase',
      marginBottom: '18px'
    },

    title: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: 'clamp(38px, 6vw, 64px)',
      fontWeight: '400',
      lineHeight: '1.08',
      color: '#2C2924',
      margin: '0 0 22px'
    },

    subtitle: {
      maxWidth: '680px',
      margin: '0 auto',
      fontSize: '16px',
      lineHeight: '1.8',
      color: '#716A60'
    },

    summary: {
      background: '#1A1815',
      borderRadius: '24px',
      padding: '32px',
      color: '#FFFFFF',
      marginBottom: '40px',
      boxShadow: '0 18px 50px rgba(40, 32, 20, 0.10)'
    },

    summaryTitle: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      color: '#D7BE84',
      fontSize: '25px',
      fontWeight: '400',
      margin: '0 0 20px'
    },

    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
      gap: '18px'
    },

    summaryItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '11px',
      color: '#D4CFC7',
      fontSize: '13px',
      lineHeight: '1.65'
    },

    section: {
      background: 'rgba(255, 255, 255, 0.78)',
      border: '1px solid #E6DED1',
      borderRadius: '20px',
      padding: '30px',
      marginBottom: '20px',
      boxShadow: '0 10px 35px rgba(70, 57, 38, 0.045)'
    },

    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      marginBottom: '17px'
    },

    iconBox: {
      width: '42px',
      height: '42px',
      minWidth: '42px',
      borderRadius: '50%',
      background: '#F1E8D8',
      color: '#A17D3D',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },

    sectionTitle: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '23px',
      fontWeight: '400',
      color: '#332F29',
      margin: 0
    },

    text: {
      color: '#716A60',
      fontSize: '14px',
      lineHeight: '1.85',
      margin: '0 0 14px'
    },

    list: {
      color: '#716A60',
      fontSize: '14px',
      lineHeight: '1.85',
      paddingLeft: '21px',
      margin: '10px 0 0'
    },

    highlight: {
      background: '#F6F0E5',
      border: '1px solid #E5D6BA',
      borderRadius: '14px',
      padding: '18px 20px',
      color: '#625747',
      fontSize: '13px',
      lineHeight: '1.75',
      marginTop: '18px'
    },

    warning: {
      background: '#FFF8ED',
      border: '1px solid #E8D5B4',
      borderRadius: '14px',
      padding: '18px 20px',
      display: 'flex',
      gap: '12px',
      alignItems: 'flex-start',
      marginTop: '18px'
    },

    warningText: {
      margin: 0,
      color: '#6D6253',
      fontSize: '13px',
      lineHeight: '1.75'
    },

    importantBox: {
      background: '#1A1815',
      color: '#FFFFFF',
      borderRadius: '16px',
      padding: '22px',
      marginTop: '20px'
    },

    importantTitle: {
      color: '#D7BE84',
      fontSize: '14px',
      fontWeight: '600',
      margin: '0 0 8px'
    },

    importantText: {
      color: '#D4CFC7',
      fontSize: '13px',
      lineHeight: '1.75',
      margin: 0
    },

    contact: {
      textAlign: 'center',
      background: '#1A1815',
      borderRadius: '22px',
      padding: '36px 25px',
      marginTop: '36px'
    },

    contactTitle: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '26px',
      color: '#D7BE84',
      margin: '0 0 10px'
    },

    contactText: {
      color: '#B9B3AA',
      fontSize: '14px',
      lineHeight: '1.7',
      margin: '0 0 20px'
    },

    contactLinks: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '16px'
    },

    contactLink: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      color: '#FFFFFF',
      textDecoration: 'none',
      fontSize: '13px'
    },

    updated: {
      textAlign: 'center',
      marginTop: '32px',
      color: '#938B7F',
      fontSize: '12px'
    }
  };

  if (!isPT) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <section style={styles.hero}>
            <div style={styles.eyebrow}>
              <FileText size={16} />
              Lou's Way
            </div>

            <h1 style={styles.title}>Terms of Service</h1>

            <p style={styles.subtitle}>
              The Portuguese version of these Terms is the reference version.
              Please change the website language to Portuguese to read the full
              Terms of Service.
            </p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* HERO */}
        <section style={styles.hero}>
          <div style={styles.eyebrow}>
            <FileText size={16} />
            Lou's Way
          </div>

          <h1 style={styles.title}>Termos de Serviço</h1>

          <p style={styles.subtitle}>
            Estes Termos regulam a utilização do website, as marcações e a
            prestação dos serviços da Lou's Way. Ao efetuar uma marcação,
            o cliente declara ter tomado conhecimento das condições aplicáveis.
          </p>
        </section>

        {/* RESUMO */}
        <section style={styles.summary}>
          <h2 style={styles.summaryTitle}>Antes da tua marcação</h2>

          <div style={styles.summaryGrid}>

            <div style={styles.summaryItem}>
              <CheckCircle2
                size={18}
                color="#BFA05F"
                style={{ minWidth: '18px' }}
              />
              <span>
                Confirma sempre o serviço, preço, data e horário antes de
                concluir a marcação.
              </span>
            </div>

            <div style={styles.summaryItem}>
              <Clock
                size={18}
                color="#BFA05F"
                style={{ minWidth: '18px' }}
              />
              <span>
                Se não puderes comparecer, avisa-nos com a maior antecedência
                possível.
              </span>
            </div>

            <div style={styles.summaryItem}>
              <AlertTriangle
                size={18}
                color="#BFA05F"
                style={{ minWidth: '18px' }}
              />
              <span>
                Em caso de no-show, será aplicada uma compensação de 30% numa
                marcação seguinte.
              </span>
            </div>

            <div style={styles.summaryItem}>
              <CreditCard
                size={18}
                color="#BFA05F"
                style={{ minWidth: '18px' }}
              />
              <span>
                A compensação de 30% é acrescida ao preço do novo serviço e
                não é descontada do seu valor.
              </span>
            </div>

          </div>
        </section>

        {/* 1 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <HeartHandshake size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              1. Identificação e âmbito
            </h2>
          </div>

          <p style={styles.text}>
            Estes Termos de Serviço aplicam-se à utilização do website
            Lou's Way e às marcações de serviços realizadas através do
            website ou diretamente com o estabelecimento.
          </p>

          <p style={styles.text}>
            A Lou's Way presta serviços de estética e beleza em Torres Vedras,
            procurando assegurar que as informações relativas aos serviços,
            preços e condições de marcação são apresentadas de forma clara
            antes da confirmação.
          </p>

          <p style={styles.text}>
            Ao efetuar uma marcação, o cliente aceita as condições aplicáveis
            ao serviço reservado, incluindo as regras relativas a
            cancelamentos, reagendamentos e faltas de comparência.
          </p>
        </section>

        {/* 2 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <CalendarDays size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              2. Marcações
            </h2>
          </div>

          <p style={styles.text}>
            As marcações estão sujeitas à disponibilidade apresentada no
            momento da reserva. O cliente é responsável por confirmar que
            selecionou corretamente o serviço, profissional, data e horário
            pretendidos.
          </p>

          <p style={styles.text}>
            A marcação apenas deverá ser considerada confirmada após a
            conclusão do respetivo processo de reserva e a disponibilização
            da confirmação pela Lou's Way.
          </p>

          <p style={styles.text}>
            O cliente deverá fornecer informações de contacto corretas e
            atualizadas para permitir a gestão da marcação e o envio das
            comunicações relacionadas com o serviço.
          </p>

          <p style={styles.text}>
            Os preços aplicáveis são os apresentados ao cliente no momento
            da marcação, salvo erro manifesto ou alteração expressamente
            comunicada e aceite antes da prestação do serviço.
          </p>
        </section>

        {/* 3 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <RefreshCcw size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              3. Cancelamentos e reagendamentos
            </h2>
          </div>

          <p style={styles.text}>
            Caso o cliente não possa comparecer, deverá cancelar ou solicitar
            o reagendamento com a maior antecedência possível através dos
            canais disponibilizados pela Lou's Way.
          </p>

          <p style={styles.text}>
            Os reagendamentos estão sempre sujeitos à disponibilidade da
            agenda.
          </p>

          <div style={styles.highlight}>
            A comunicação antecipada permite à Lou's Way disponibilizar o
            horário a outra cliente e organizar adequadamente a agenda do
            estúdio.
          </div>
        </section>

        {/* 4 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <Ban size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              4. Falta de comparência — No-show
            </h2>
          </div>

          <p style={styles.text}>
            Considera-se falta de comparência (no-show) quando o cliente não
            comparece à marcação e não comunica previamente a impossibilidade
            de comparecer.
          </p>

          <p style={styles.text}>
            Cada marcação corresponde à reserva exclusiva de um período da
            agenda da Lou's Way. Durante esse período, o horário deixa de
            estar disponível para marcação por outros clientes.
          </p>

          <p style={styles.text}>
            Uma falta de comparência sem aviso pode, por isso, representar
            uma perda económica para a Lou's Way, uma vez que o período
            reservado poderia ter sido utilizado para a prestação de um
            serviço a outro cliente.
          </p>

          <div style={styles.warning}>
            <AlertTriangle
              size={20}
              color="#A17D3D"
              style={{
                minWidth: '20px',
                marginTop: '2px'
              }}
            />

            <p style={styles.warningText}>
              <strong>
                Em caso de falta de comparência sem cancelamento prévio,
                será aplicada uma compensação correspondente a 30% do valor
                do serviço numa marcação seguinte.
              </strong>

              <br />
              <br />

              Assim, ao efetuar uma nova marcação após um no-show, será
              devido o preço normal do novo serviço acrescido de 30%.

              <br />
              <br />

              Este valor destina-se a compensar parcialmente a perda do
              período anteriormente reservado e não utilizado, durante o
              qual a Lou's Way ficou impossibilitada de disponibilizar
              aquele horário a outro cliente.
            </p>
          </div>
        </section>

        {/* 5 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <CreditCard size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              5. Compensação de 30% por no-show
            </h2>
          </div>

          <p style={styles.text}>
            Quando tenha ocorrido uma falta de comparência nos termos
            anteriormente descritos, a marcação seguinte ficará sujeita
            a uma compensação correspondente a 30% do preço do novo serviço.
          </p>

          <ul style={styles.list}>
            <li>
              a compensação corresponde a 30% do preço do serviço da nova
              marcação;
            </li>

            <li>
              o valor é acrescido ao preço normal do novo serviço;
            </li>

            <li>
              a compensação não constitui um adiantamento ou sinal relativo
              ao novo serviço;
            </li>

            <li>
              o valor não é descontado do preço do novo serviço;
            </li>

            <li>
              a compensação não é reembolsada pelo simples facto de o
              cliente comparecer à nova marcação;
            </li>

            <li>
              a compensação destina-se a ressarcir parcialmente a Lou's Way
              pelo prejuízo decorrente da reserva anterior não utilizada;
            </li>

            <li>
              a existência da compensação deverá ser comunicada ao cliente
              antes da confirmação da nova marcação.
            </li>
          </ul>

          <div style={styles.importantBox}>
            <p style={styles.importantTitle}>
              Exemplo prático
            </p>

            <p style={styles.importantText}>
              Se uma cliente tiver um no-show e posteriormente efetuar uma
              nova marcação para um serviço com o preço de €50, será aplicado
              o preço do novo serviço (€50) acrescido da compensação de 30%
              (€15), totalizando €65.
            </p>
          </div>

          <p style={{ ...styles.text, marginTop: '20px' }}>
            Situações excecionais devidamente justificadas poderão ser
            analisadas individualmente pela Lou's Way.
          </p>
        </section>

        {/* 6 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <Clock size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              6. Atrasos
            </h2>
          </div>

          <p style={styles.text}>
            Pedimos que o cliente compareça à hora marcada.
          </p>

          <p style={styles.text}>
            Em caso de atraso, a Lou's Way procurará realizar o serviço sempre
            que tal seja possível sem prejudicar a qualidade do atendimento,
            a segurança do procedimento ou as marcações seguintes.
          </p>

          <p style={styles.text}>
            Quando o atraso impossibilite objetivamente a realização adequada
            do serviço dentro do período reservado, poderá ser necessário
            reagendar a marcação.
          </p>
        </section>

        {/* 7 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <ShieldCheck size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              7. Condições dos serviços
            </h2>
          </div>

          <p style={styles.text}>
            Alguns serviços podem depender de avaliação prévia e poderão não
            ser realizados quando existam condições que comprometam a
            segurança, adequação ou resultado do procedimento.
          </p>

          <p style={styles.text}>
            O cliente deverá comunicar informações relevantes que possam
            interferir com a realização segura do serviço sempre que estas
            lhe sejam solicitadas.
          </p>

          <p style={styles.text}>
            A Lou's Way poderá recusar ou interromper um procedimento quando
            considere que não estão reunidas condições adequadas para a sua
            realização em segurança.
          </p>

          <p style={styles.text}>
            Os resultados dos tratamentos estéticos podem variar de pessoa
            para pessoa em função das características individuais, resposta
            ao procedimento, cuidados posteriores e outros fatores.
          </p>
        </section>

        {/* 8 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <CreditCard size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              8. Preços e pagamentos
            </h2>
          </div>

          <p style={styles.text}>
            Os preços dos serviços são apresentados no website e/ou durante
            o processo de marcação.
          </p>

          <p style={styles.text}>
            A Lou's Way poderá atualizar os preços dos seus serviços. As
            alterações produzirão efeitos para novas marcações realizadas
            após a entrada em vigor dos novos preços, salvo indicação
            expressa em contrário.
          </p>

          <p style={styles.text}>
            Os meios de pagamento disponíveis são os apresentados ao cliente
            no website, durante o processo de marcação ou no estabelecimento.
          </p>

          <p style={styles.text}>
            Eventuais valores adicionais decorrentes da política de no-show
            serão identificados e comunicados ao cliente nos termos destes
            Termos de Serviço.
          </p>
        </section>

        {/* 9 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <ShieldCheck size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              9. Dados pessoais
            </h2>
          </div>

          <p style={styles.text}>
            Os dados pessoais necessários à gestão da conta, marcações,
            comunicações e pagamentos são tratados de acordo com a Política
            de Privacidade da Lou's Way e com a legislação aplicável em
            matéria de proteção de dados.
          </p>

          <p style={styles.text}>
            O utilizador poderá consultar a Política de Privacidade e a
            página de Exclusão de Dados do Utilizador disponíveis no website.
          </p>
        </section>

        {/* 10 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <Scale size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              10. Reclamações e resolução de litígios
            </h2>
          </div>

          <p style={styles.text}>
            Em caso de questão ou reclamação relacionada com uma marcação,
            pagamento ou serviço, recomendamos que o cliente contacte
            inicialmente a Lou's Way para que possamos analisar a situação
            e procurar uma solução.
          </p>

          <p style={styles.text}>
            O consumidor poderá ainda recorrer aos mecanismos de resolução
            alternativa de litígios de consumo legalmente disponíveis e às
            entidades competentes nos termos da legislação portuguesa.
          </p>

          <div style={styles.highlight}>
            A informação relativa à entidade de Resolução Alternativa de
            Litígios (RAL) competente será disponibilizada pela Lou's Way
            nos termos da legislação portuguesa aplicável.
          </div>
        </section>

        {/* 11 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <FileText size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              11. Alterações aos Termos
            </h2>
          </div>

          <p style={styles.text}>
            A Lou's Way poderá atualizar estes Termos de Serviço sempre que
            tal seja necessário para refletir alterações nos serviços,
            funcionamento do website, políticas de marcação ou requisitos
            legais.
          </p>

          <p style={styles.text}>
            As condições aplicáveis a uma marcação são, em princípio, as
            condições apresentadas ao cliente no momento da respetiva
            contratação, sem prejuízo das alterações exigidas pela legislação
            aplicável.
          </p>
        </section>

        {/* 12 */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <div style={styles.iconBox}>
              <Scale size={20} />
            </div>

            <h2 style={styles.sectionTitle}>
              12. Lei aplicável
            </h2>
          </div>

          <p style={styles.text}>
            Estes Termos são regidos pela legislação portuguesa, sem prejuízo
            dos direitos imperativos conferidos ao consumidor pela legislação
            nacional e europeia aplicável.
          </p>
        </section>

        {/* CONTACTOS */}
        <section style={styles.contact}>
          <h2 style={styles.contactTitle}>
            Ficou com alguma dúvida?
          </h2>

          <p style={styles.contactText}>
            Entre em contacto com a Lou's Way. Teremos todo o gosto em
            esclarecer qualquer questão relacionada com os nossos serviços,
            marcações ou estes Termos de Serviço.
          </p>

          <div style={styles.contactLinks}>
            <a
              href="mailto:lourdyane.rod@gmail.com"
              style={styles.contactLink}
            >
              <Mail size={16} color="#BFA05F" />
              lourdyane.rod@gmail.com
            </a>

            <span style={styles.contactLink}>
              <MapPin size={16} color="#BFA05F" />
              Torres Vedras, Portugal
            </span>
          </div>
        </section>

        <p style={styles.updated}>
          Última atualização: 13 de outubro de 2026
        </p>

      </div>
    </div>
  );
};

export default TermosServico;